import styled from 'styled-components';
import {
  Children,
  Fragment,
  isValidElement,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, IconButton, Panel, Switch } from '@/ui';
import { Icon } from '@/icons';
import { SurfaceScope } from '@/theme/SurfaceScope';
import { useSessionStore } from '@/core/stores/sessionStore';
import {
  readRailLayouts,
  resolveRailLayout,
  useRailLayoutsStore,
} from '@/core/stores/railLayoutsStore';
import type { RailPageKey } from '@/core/stores/railLayoutsStore';
import { RailToolbarContext } from './context';
import { RailHeader } from './RailHeader';
import { RailHeaderButton } from './RailHeader.styles';

// Each panel owns its surface, allowing the page background to show between cards.
const Group = styled.div.attrs<{ 'data-sidebar'?: boolean }>({ 'data-sidebar': true })`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  > * {
    margin: 0;
    min-width: 0;
  }
  h2 {
    font-family: ${({ theme }) => theme.font.display};
    text-transform: none;
    letter-spacing: normal;
    font-size: ${({ theme }) => theme.fontSize.sm};
  }
  h2::before {
    display: none;
  }
`;

export interface RailPanelDefinition {
  id: string;
  label: string;
  required?: boolean;
  defaultHidden?: boolean;
  content: ReactNode;
}
export interface RailPanelProps extends Omit<RailPanelDefinition, 'content'> {
  children: ReactNode;
}
/** Declarative panel definition; the group renders its content in saved order. */
export function RailPanel({ children }: RailPanelProps) {
  return children;
}

function definitions(children: ReactNode): RailPanelDefinition[] {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement<{ children?: ReactNode }>(child)) return [];
    if (child.type === Fragment) return definitions(child.props.children);
    if (isValidElement<RailPanelProps>(child) && child.type === RailPanel) {
      const { children: content, ...metadata } = child.props;
      return [{ ...metadata, content }];
    }
    return [];
  });
}

const Editor = styled(Panel)`
  padding: 12px;
  scroll-margin-top: 72px;
  h3 {
    margin: 0 0 8px;
    font-size: ${({ theme }) => theme.fontSize.sm};
  }
  ol {
    list-style: none;
    padding: 0;
    margin: 0 0 10px;
  }
`;
const EditRow = styled.li`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  > span {
    flex: 1;
    min-width: 0;
    font-size: ${({ theme }) => theme.fontSize.xs};
  }
  small {
    display: block;
    color: ${({ theme }) => theme.color.textMuted};
  }
  > button:not([role='switch']) {
    width: 30px;
    height: 36px;
    flex-shrink: 0;
  }
  &:focus {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;
const EditorActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;
const Announcement = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

function ConfigurableRail({
  owner,
  pageKey,
  panels,
  headerTitle,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  owner: string;
  pageKey: RailPageKey;
  panels: RailPanelDefinition[];
  headerTitle: string;
}) {
  const initial = useMemo(() => readRailLayouts(owner), [owner]);
  const savedPages = useRailLayoutsStore((state) => state.accounts[owner]);
  const save = useRailLayoutsStore((state) => state.save);
  const layout = resolveRailLayout(panels, (savedPages ?? initial)[pageKey]);
  const byId = new Map(panels.map((panel) => [panel.id, panel]));
  const [editing, setEditing] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const rows = useRef(new Map<string, HTMLLIElement>());
  const focusRow = useRef<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const editorId = useId();
  const toolbar = useContext(RailToolbarContext);
  const editable = panels.length > 1 || panels.some((panel) => !panel.required);
  useLayoutEffect(() => {
    if (editing) {
      editorRef.current?.scrollIntoView?.({ block: 'nearest' });
      editorRef.current?.focus({ preventScroll: true });
    }
  }, [editing]);
  useLayoutEffect(() => {
    if (focusRow.current) {
      rows.current.get(focusRow.current)?.focus();
      focusRow.current = null;
    }
  });
  function move(id: string, delta: number) {
    const order = [...layout.order];
    const index = order.indexOf(id);
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    [order[index], order[target]] = [order[target]!, order[index]!];
    focusRow.current = id;
    save(owner, pageKey, { ...layout, order });
    setAnnouncement(`${byId.get(id)!.label} moved to position ${target + 1} of ${order.length}`);
  }
  const trigger = editable ? (
    <RailHeaderButton
      ref={triggerRef}
      type="button"
      aria-expanded={editing}
      aria-controls={editorId}
      aria-label="Customize panels"
      title="Customize panels"
      data-customizing={editing}
      onClick={() => setEditing(!editing)}
    >
      <Icon name="sliders-horizontal" size={18} />
    </RailHeaderButton>
  ) : null;
  return (
    <SurfaceScope role="panel">
      <Group {...props} data-rail-page={pageKey}>
        {toolbar
          ? createPortal(trigger, toolbar)
          : (trigger || headerTitle !== 'Panels') && (
              <RailHeader title={headerTitle}>{trigger}</RailHeader>
            )}
        {editing && (
          <Editor
            ref={editorRef}
            tabIndex={-1}
            id={editorId}
            role="region"
            aria-label="Customize panels"
          >
            <h3>Customize panels</h3>
            <ol>
              {layout.order.map((id, index) => {
                const panel = byId.get(id)!;
                return (
                  <EditRow
                    key={id}
                    tabIndex={-1}
                    ref={(el) => {
                      if (el) rows.current.set(id, el);
                      else rows.current.delete(id);
                    }}
                  >
                    <span>
                      {panel.label}
                      {panel.required && <small>Always visible</small>}
                    </span>
                    <Switch
                      label={`Show ${panel.label}`}
                      checked={!layout.hidden.includes(id)}
                      disabled={panel.required}
                      onChange={(show) =>
                        save(owner, pageKey, {
                          ...layout,
                          hidden: show
                            ? layout.hidden.filter((key) => key !== id)
                            : [...layout.hidden, id],
                        })
                      }
                    />
                    <IconButton
                      icon="arrow-up"
                      size={16}
                      label={`Move ${panel.label} up`}
                      disabled={index === 0}
                      onClick={() => move(id, -1)}
                    />
                    <IconButton
                      icon="arrow-down"
                      size={16}
                      label={`Move ${panel.label} down`}
                      disabled={index === layout.order.length - 1}
                      onClick={() => move(id, 1)}
                    />
                  </EditRow>
                );
              })}
            </ol>
            <EditorActions>
              <Button
                variant="ghost"
                onClick={() => {
                  save(owner, pageKey, null);
                  setAnnouncement('Default panels restored');
                }}
              >
                Restore defaults
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(false);
                  triggerRef.current?.focus();
                }}
              >
                Done
              </Button>
            </EditorActions>
          </Editor>
        )}
        <Announcement role="status">{announcement}</Announcement>
        {layout.order
          .filter((id) => !layout.hidden.includes(id))
          .map((id) => (
            <Fragment key={id}>{byId.get(id)!.content}</Fragment>
          ))}
        {layout.order.length > 0 && layout.hidden.length === layout.order.length && (
          <Panel>No panels selected. Use Customize panels to restore them.</Panel>
        )}
      </Group>
    </SurfaceScope>
  );
}

export function RailGroup({
  pageKey,
  headerTitle = 'Panels',
  children,
  ...props
}: HTMLAttributes<HTMLDivElement> & { pageKey?: RailPageKey; headerTitle?: string }) {
  const owner = useSessionStore(
    (state) => state.accounts.find((account) => account.useThisAsActive)?.handle ?? 'guest',
  );
  if (!pageKey)
    return (
      <SurfaceScope role="panel">
        <Group {...props}>{children}</Group>
      </SurfaceScope>
    );
  return (
    <ConfigurableRail
      key={`${owner}:${pageKey}`}
      owner={owner}
      pageKey={pageKey}
      panels={definitions(children)}
      headerTitle={headerTitle}
      {...props}
    />
  );
}
